/**
 * @file useRecorderLeaveGuard.ts
 * @brief Leave warning for boardroom capture — desktop/laptop browsers first.
 *
 * @details
 * Primary path is React Router `useBlocker` (works with createBrowserRouter on
 * desktop Safari + Chrome + Edge). When blocked, Safari/WebKit gets a sync
 * `window.confirm`; Chromium gets our AlertDialog.
 *
 * Refresh/tab close uses `window.onbeforeunload` (property — required by Safari).
 *
 * This is for WEBPANEL in desktop/laptop browsers, not the native mobile app.
 * iPhone Safari may still ignore refresh prompts (Apple policy).
 *
 * @author DES
 * @date 28-07-2026
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker, useNavigate } from "react-router-dom";
import { isSafariWebKit } from "./capture/safariDetect";

const MSG =
  "Leave this recording?\n\nAudio already saved locally can be recovered from Meeting Intel.";

export function useRecorderLeaveGuard(
  active: boolean,
  leaveFallbackPath: string,
) {
  const navigate = useNavigate();
  const safari = isSafariWebKit();
  const allowLeaveRef = useRef(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [protectionArmed, setProtectionArmed] = useState(false);

  const allowLeave = useCallback(() => {
    allowLeaveRef.current = true;
    setProtectionArmed(false);
  }, []);

  // Re-arm whenever we become active again.
  useEffect(() => {
    if (!active) {
      setProtectionArmed(false);
      return;
    }
    allowLeaveRef.current = false;
    setProtectionArmed(true);
  }, [active]);

  // Refresh / close tab — desktop Safari needs the property form.
  useEffect(() => {
    if (!active) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (allowLeaveRef.current) return;
      e.preventDefault();
      e.returnValue = MSG;
      return MSG;
    };

    window.onbeforeunload = onBeforeUnload;
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      if (window.onbeforeunload === onBeforeUnload) {
        window.onbeforeunload = null;
      }
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [active]);

  // Back / Forward / in-app links — React Router blocker (desktop Safari + Chrome).
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!active || allowLeaveRef.current) return false;
    return (
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search
    );
  });

  useEffect(() => {
    if (blocker.state !== "blocked") return;

    // Desktop Safari: sync confirm — AlertDialog alone was easy to miss / race.
    if (safari) {
      const leave = window.confirm(MSG);
      if (leave) {
        allowLeaveRef.current = true;
        setProtectionArmed(false);
        blocker.proceed();
      } else {
        blocker.reset();
      }
      return;
    }

    setLeaveDialogOpen(true);
  }, [blocker, safari]);

  const stayOnPage = useCallback(() => {
    setLeaveDialogOpen(false);
    if (blocker.state === "blocked") blocker.reset();
  }, [blocker]);

  const confirmLeave = useCallback(() => {
    allowLeaveRef.current = true;
    setProtectionArmed(false);
    setLeaveDialogOpen(false);
    if (blocker.state === "blocked") {
      blocker.proceed();
      return;
    }
    navigate(leaveFallbackPath, { replace: true });
  }, [blocker, leaveFallbackPath, navigate]);

  const onLeaveDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setLeaveDialogOpen(true);
        return;
      }
      stayOnPage();
    },
    [stayOnPage],
  );

  return {
    leaveDialogOpen: safari ? false : leaveDialogOpen,
    allowLeave,
    stayOnPage,
    confirmLeave,
    setLeaveDialogOpen: onLeaveDialogOpenChange,
    protectionArmed: active && protectionArmed,
  };
}
